import React from "react";
import { Box, Card, CardContent, CardMedia, Typography } from "@mui/material";
import { useLanding } from "../context/LandingContext";
import { navbarOffsetSx, resolveNavbarStyle } from "../utils/landingLayout";
import { handleLandingMediaError, resolveLandingMedia } from "../utils/landingMedia";
import LandingHighlightIcon from "./LandingHighlightIcon";
import { safeTextSx, safeTitleSx, sectionTitleFontSize, titleWordFitScale } from "../utils/textSafety";
import { normalizeImagePosition, scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { youtubeEmbedUrl } from "../utils/youtube";
import { capitalizeSectionTitle } from "../utils/landingSectionText";
import { HIGHLIGHT_VISUAL_DEFAULTS } from "../utils/highlightVisualOwnership";

function clamp01(value, fallback = 0.24) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(1, number));
}

function finiteValue(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function LandingHighlightsSection({ sectionDomId = "highlights" }) {
  const { highlights, highlightsIntro, theme, navigation, sections } = useLanding();
  const navbarOffset = navbarOffsetSx(resolveNavbarStyle(navigation, theme), sections?.showNavbar !== false);
  const items = Array.isArray(highlights)
    ? highlights.filter((item) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl)
    : [];
  const backgroundColor = highlightsIntro?.backgroundColor;
  const accentColor = highlightsIntro?.eyebrowColor;
  const titleColor = highlightsIntro?.titleColor;
  const textColor = highlightsIntro?.subtitleColor;
  const backgroundImage = resolveLandingMedia(highlightsIntro?.backgroundImageUrl);
  const backgroundImagePosition = normalizeImagePosition(highlightsIntro?.backgroundImagePosition);
  const backgroundImageOpacity = clamp01(highlightsIntro?.backgroundImageOpacity, 0.24);
  const titleScale = segmentVisualScale(theme, "highlights", "title");
  const imageScale = segmentVisualScale(theme, "highlights", "image");
  const spacingScale = segmentVisualScale(theme, "highlights", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(highlightsIntro?.title, { xs: "2.05rem", md: "2.75rem" }),
    titleScale
  );
  const hasIntro = Boolean(highlightsIntro?.eyebrow || highlightsIntro?.title || highlightsIntro?.subtitle);
  const desktopColumns = Math.max(1, Math.min(items.length, 4));

  if (!items.length && !hasIntro) return null;

  return (
    <Box
      component="section"
      id={sectionDomId}
      data-landing-section="highlights"
      sx={{
        position: "relative",
        overflow: "hidden",
        px: { xs: 2.5, md: 8 },
        py: { xs: 7 * spacingScale, md: 9 * spacingScale },
        bgcolor: backgroundColor,
        scrollMarginTop: navbarOffset,
      }}
    >
      {backgroundImage ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: backgroundImagePosition,
            backgroundRepeat: "no-repeat",
            opacity: backgroundImageOpacity,
            pointerEvents: "none",
          }}
        />
      ) : null}

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1320, mx: "auto" }}>
        {highlightsIntro?.eyebrow ? (
          <Typography data-landing-field="highlightsIntro.eyebrow" variant="overline" sx={{ ...safeTextSx, color: accentColor, fontWeight: 900, letterSpacing: ".16em" }}>
            {highlightsIntro.eyebrow}
          </Typography>
        ) : null}

        {highlightsIntro?.title ? (
          <Typography
            data-landing-field="highlightsIntro.title"
            variant="h4"
            sx={{
              ...safeTitleSx,
              color: titleColor,
              fontSize: titleSize,
              fontWeight: "var(--wp-segment-title-weight)",
              letterSpacing: "var(--wp-segment-letter-spacing)",
              mb: highlightsIntro?.subtitle ? 1 : items.length ? 4 : 0,
            }}
          >
            {capitalizeSectionTitle(highlightsIntro.title)}
          </Typography>
        ) : null}

        {highlightsIntro?.subtitle ? (
          <Typography data-landing-field="highlightsIntro.subtitle" sx={{ ...safeTextSx, color: textColor, lineHeight: 1.7, mb: items.length ? 4 : 0, maxWidth: 800, whiteSpace: "pre-line" }}>
            {highlightsIntro.subtitle}
          </Typography>
        ) : null}

        {items.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: items.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                lg: `repeat(${desktopColumns}, minmax(0, 1fr))`,
              },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            {items.map((item, index) => {
              const itemId = item.id || `diferencial-${index + 1}`;
              const cardImage = resolveLandingMedia(item.imageUrl);
              const embedUrl = youtubeEmbedUrl(item.youtubeUrl);
              const hasVisualMedia = Boolean(embedUrl || cardImage);
              const cardStyle = item.cardStyle || HIGHLIGHT_VISUAL_DEFAULTS.cardStyle;
              const iconStyle = item.iconStyle || HIGHLIGHT_VISUAL_DEFAULTS.iconStyle;
              const iconSurfaceStyle = item.iconSurfaceStyle || HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle;
              const titleStyle = item.titleStyle || HIGHLIGHT_VISUAL_DEFAULTS.titleStyle;
              const descriptionStyle = item.descriptionStyle || HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle;
              const imageStyle = item.imageStyle || HIGHLIGHT_VISUAL_DEFAULTS.imageStyle;
              const videoStyle = item.videoStyle || HIGHLIGHT_VISUAL_DEFAULTS.videoStyle;
              const contentPadding = finiteValue(cardStyle.contentPadding, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.contentPadding);
              const mediaIconWidth = finiteValue(iconSurfaceStyle.mediaWidth, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaWidth);
              const mediaIconHeight = finiteValue(iconSurfaceStyle.mediaHeight, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaHeight);
              const iconSurfaceBorder = `${finiteValue(iconSurfaceStyle.borderWidth, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderWidth)}px solid ${iconSurfaceStyle.borderColor}`;
              return (
                <Card
                  key={itemId}
                  data-highlight-id={itemId}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    height: "100%",
                    borderRadius: `${finiteValue(cardStyle.borderRadius, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderRadius)}px`,
                    border: `${finiteValue(cardStyle.borderWidth, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderWidth)}px solid ${cardStyle.borderColor}`,
                    overflow: "hidden",
                    bgcolor: cardStyle.backgroundColor,
                    boxShadow: cardStyle.boxShadow,
                    transition: "transform .24s ease, box-shadow .24s ease",
                    "&:hover": { transform: `translateY(${finiteValue(cardStyle.hoverTranslateY, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.hoverTranslateY)}px)`, boxShadow: cardStyle.hoverBoxShadow },
                  }}
                >
                  {embedUrl ? (
                    <Box
                      component="iframe"
                      src={embedUrl}
                      title={item.title || `Video del diferencial ${index + 1}`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      sx={{ width: "100%", aspectRatio: videoStyle.aspectRatio, display: "block", border: 0, bgcolor: videoStyle.backgroundColor }}
                    />
                  ) : cardImage ? (
                    <Box sx={{ position: "relative", bgcolor: imageStyle.backgroundColor }}>
                      <CardMedia
                        component="img"
                        height={(finiteValue(imageStyle.height, HIGHLIGHT_VISUAL_DEFAULTS.imageStyle.height)) * imageScale}
                        image={cardImage}
                        alt={item.imageAlt ?? ""}
                        loading="lazy"
                        decoding="async"
                        onError={handleLandingMediaError}
                        sx={{ objectFit: imageStyle.objectFit, objectPosition: normalizeImagePosition(item.imagePosition), bgcolor: imageStyle.backgroundColor }}
                      />
                      {item.icon ? (
                        <Box
                          sx={{
                            position: "absolute",
                            left: 20,
                            bottom: -(mediaIconHeight / 2),
                            width: mediaIconWidth,
                            height: mediaIconHeight,
                            borderRadius: `${finiteValue(iconSurfaceStyle.mediaBorderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaBorderRadius)}px`,
                            display: "grid",
                            placeItems: "center",
                            color: iconStyle.color,
                            bgcolor: iconSurfaceStyle.floatingBackgroundColor,
                            border: iconSurfaceBorder,
                            boxShadow: iconSurfaceStyle.floatingBoxShadow,
                          }}
                        >
                          <LandingHighlightIcon name={item.icon} sx={{ fontSize: finiteValue(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                        </Box>
                      ) : null}
                    </Box>
                  ) : item.icon ? (
                    <Box sx={{ px: `${contentPadding}px`, pt: `${contentPadding}px` }}>
                      <Box sx={{ width: finiteValue(iconSurfaceStyle.width, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.width), height: finiteValue(iconSurfaceStyle.height, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.height), borderRadius: `${finiteValue(iconSurfaceStyle.borderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderRadius)}px`, display: "grid", placeItems: "center", color: iconStyle.color, bgcolor: iconSurfaceStyle.backgroundColor, border: iconSurfaceBorder, boxShadow: iconSurfaceStyle.boxShadow }}>
                        <LandingHighlightIcon name={item.icon} sx={{ fontSize: finiteValue(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                      </Box>
                    </Box>
                  ) : null}

                  {embedUrl && item.icon ? (
                    <Box sx={{ px: `${contentPadding}px`, pt: `${Math.max(12, contentPadding * .72)}px` }}>
                      <Box sx={{ width: mediaIconWidth, height: mediaIconHeight, borderRadius: `${finiteValue(iconSurfaceStyle.mediaBorderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaBorderRadius)}px`, display: "grid", placeItems: "center", color: iconStyle.color, bgcolor: iconSurfaceStyle.backgroundColor, border: iconSurfaceBorder, boxShadow: iconSurfaceStyle.boxShadow }}>
                        <LandingHighlightIcon name={item.icon} sx={{ fontSize: finiteValue(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                      </Box>
                    </Box>
                  ) : null}

                  {item.title || item.description ? (
                    <CardContent sx={{ minWidth: 0, p: `${contentPadding}px`, pt: cardImage && item.icon ? `${contentPadding + Math.round(mediaIconHeight / 3)}px` : embedUrl && item.icon ? `${Math.max(12, contentPadding * .7)}px` : hasVisualMedia ? `${contentPadding}px` : item.icon ? `${Math.max(12, contentPadding * .72)}px` : `${contentPadding}px` }}>
                      {item.title ? <Typography data-landing-field={`highlights.${index}.title`} sx={{ ...safeTitleSx, fontSize: `${(finiteValue(titleStyle.fontSize, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontSize)) * titleWordFitScale(item.title)}px`, fontWeight: finiteValue(titleStyle.fontWeight, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontWeight), lineHeight: finiteValue(titleStyle.lineHeight, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.lineHeight), color: titleStyle.color }}>{item.title}</Typography> : null}
                      {item.description ? (
                        <Typography data-landing-field={`highlights.${index}.description`} sx={{ ...safeTextSx, mt: item.title ? 1 : 0, color: descriptionStyle.color, fontSize: `${finiteValue(descriptionStyle.fontSize, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontSize)}px`, fontWeight: finiteValue(descriptionStyle.fontWeight, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontWeight), lineHeight: finiteValue(descriptionStyle.lineHeight, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.lineHeight), whiteSpace: "pre-line" }}>
                          {item.description}
                        </Typography>
                      ) : null}
                    </CardContent>
                  ) : null}
                </Card>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default LandingHighlightsSection;
